import { For, Match, Show, Switch, createEffect, createMemo, createSignal, lazy, onMount } from "solid-js";
import { CadWorkspace, type WorkspaceSelection } from "./components/CadWorkspace";
import { PrintStudio } from "./components/PrintStudio";
import { PrintSetPage } from "./components/PrintSetPage";
import { PatternLibrary } from "./components/PatternLibrary";
import { FitPage } from "./components/FitPage";
import { StartPage, type StartMenuItem } from "./components/StartPage";
import type { BodySource, SavedProfileSummary } from "./components/BodyPicker";
import { StylePanel } from "./components/StylePanel";
import { GoalBar, GuideDialog, guideSteps, type GuideStep } from "./components/StepGuide";
import { CheckIcon, NoPrintIcon, PrintIcon, StepIcon } from "./components/Icons";
import { draftPattern, normaliseOptionValue, type BodyMeasurements, type Diagnostic, type OptionValue } from "@tailor-frame/engine";
import { bodyMeasurementFor, bodyMeasurementRows, bodyMeasurementsFor, bodyTypeForProfile, bodyTypes, defaultsFor, linkedMeasurements, measurementProfiles, parsePattern, bodyProfileIds, sizeChartProfile, sizeChartsFor, specifications, type BodyMeasurementSet, type BodyProfileId, type BodyType, type ChartStature, type MeasurementProfile } from "@tailor-frame/patterns";
import { pieceColourCss } from "./core/pieceColours";
import { projectForPattern, savePatternSettings } from "./core/patternSettings";
import { printSetById, printSets } from "./core/printSets";
import { linkClick, navigate, route, routePath, type PatternView } from "./core/router";
import { NewProjectPage, ProjectPage, ProjectsPage, SkillsPage } from "./components/Workshop";
import { optionsFor, recipeById } from "./core/workshop/recipes";
import { draftAdjustments, loadProjects, newProject, nextStep, progressOf, projectPlan, saveProjects, type Project } from "./core/workshop/projects";
import { loadSkillProgress, raiseSkill, saveSkillProgress, skillForGuide, skills, type SkillLevel } from "./core/workshop/skills";

/** Loaded on first visit, keeping three.js out of the rest of the app. */
const PocketWizard = lazy(() => import("./components/PocketWizard").then((module) => ({ default: module.PocketWizard })));

/**
 * The client's body: one set of measurements every pattern reads from, kept
 * between visits. It starts from a body type and a size (or a profile), is
 * customised, and can be saved as a named profile.
 */
interface SavedBody { source: BodySource; bodyType: BodyType; madeFor: string; values: BodyMeasurementSet; estimated: string[]; figure?: BodyMeasurements; sex: "male" | "female" }
/** A body saved by name on the measurements page. */
interface SavedProfile extends Omit<SavedBody, "source"> { id: string; from?: string }
const bodyStorageKey = "tailor-frame:body";
const profilesStorageKey = "tailor-frame:profiles";
const recoveryStorageKey = "tailor-frame:recovery-source";
// Edits saved before the rename from Pixi Pyjama; read as a fallback and cleared on the next save.
const legacyRecoveryStorageKey = "pixi-pyjama:recovery-source";
// Set once the guide has been seen, so it only opens by itself on a first visit.
const guideSeenStorageKey = "tailor-frame:guide-seen";
// Whether patterns are cut with bespoke inlay, kept as a preference across patterns.
const inlayStorageKey = "tailor-frame:include-inlay";

// Pieces left out of the print, by pattern id, so a pattern opens with the same pieces turned off.
const printExcludedStorageKey = "tailor-frame:print-excluded";

function loadPrintExcluded(): Record<string, string[]> {
  try { return JSON.parse(localStorage.getItem(printExcludedStorageKey) ?? "{}") as Record<string, string[]>; } catch { return {}; }
}

function loadIncludeInlay(): boolean {
  try { return localStorage.getItem(inlayStorageKey) === "1"; } catch { return false; }
}

// Head proportions for an adult chart size, from the example figures, scaled to the height.
const adultHeads = { male: measurementProfiles.chris.stature!, female: measurementProfiles.anca.stature! };

function bodyFrom(profile: MeasurementProfile, source: BodySource): SavedBody {
  const { values, estimated } = bodyMeasurementsFor(profile);
  let figure = profile.stature ? { ...profile.stature } : undefined;
  if (!figure && values.height !== undefined && (profile.sizeRange ?? "adult") === "adult") {
    const head = adultHeads[profile.figure], scale = values.height / head.height;
    figure = { height: values.height, headHeight: Math.round(head.headHeight * scale), headWidth: Math.round(head.headWidth * scale), headDepth: Math.round(head.headDepth * scale) };
  }
  return { source, bodyType: bodyTypeForProfile(profile), madeFor: profile.label, values, estimated, sex: profile.figure, figure };
}

function bodyFromProfile(profileId: BodyProfileId): SavedBody {
  return bodyFrom(measurementProfiles[profileId], { kind: "example", id: profileId });
}

function bodyFromChart(chartId: string, sizeId: string, stature: ChartStature["id"]): SavedBody | undefined {
  const profile = sizeChartProfile(chartId, sizeId, stature);
  return profile && bodyFrom(profile, { kind: "chart", chartId, sizeId, stature });
}

/** A body type's starting body: its first chart's default size, regular height. */
function bodyForType(type: BodyType): SavedBody {
  const chart = sizeChartsFor(type)[0];
  return bodyFromChart(chart.id, chart.defaultSize, "regular")!;
}

/** The name a body keeps once edited: its profile's name, marked as changed. */
function editedName(body: SavedBody): string {
  if (body.source.kind === "custom") return body.madeFor;
  return body.source.kind === "saved" ? body.madeFor : `${body.madeFor} (edited)`;
}

function loadSavedProfiles(): SavedProfile[] {
  try { return JSON.parse(localStorage.getItem(profilesStorageKey) ?? "[]") as SavedProfile[]; } catch { return []; }
}

function loadSavedBody(): SavedBody {
  const fallback = bodyFromProfile("chris");
  try {
    const saved = JSON.parse(localStorage.getItem(bodyStorageKey) ?? "null") as (SavedBody & { profile?: BodyProfileId | "custom" }) | null;
    if (saved?.values) {
      // Bodies saved before body types name a profile id (or "custom") instead of a source.
      const profileId = saved.profile !== undefined && saved.profile !== "custom" && bodyProfileIds.includes(saved.profile) ? saved.profile : undefined;
      const source: BodySource = saved.source ?? (profileId ? { kind: "example", id: profileId } : { kind: "custom" });
      // Bodies saved before the figure was stored take it from their profile.
      const sex = saved.sex ?? (profileId ? measurementProfiles[profileId].figure : fallback.sex);
      const figure = saved.figure ?? (profileId ? measurementProfiles[profileId].stature : undefined);
      const bodyType = saved.bodyType ?? (profileId ? bodyTypeForProfile(measurementProfiles[profileId]) : sex === "female" ? "feminine" : "masculine");
      // Bodies saved with one garment length keep it as the shirt length; jacket and coat lengths are estimated.
      const { garmentLength, ...values } = saved.values as BodyMeasurementSet & { garmentLength?: number };
      if (garmentLength !== undefined && values.shirtLength === undefined) values.shirtLength = garmentLength;
      const missing = (Object.keys(fallback.values) as (keyof BodyMeasurementSet)[]).filter((id) => values[id] === undefined);
      const estimated = [...new Set([...(saved.estimated ?? []).filter((id) => id !== "garmentLength"), ...missing])];
      const { profile: _legacy, ...rest } = saved;
      // An unedited example takes its current label (the examples go by initials).
      const madeFor = source.kind === "example" && bodyProfileIds.includes(source.id as BodyProfileId) ? measurementProfiles[source.id as BodyProfileId].label : saved.madeFor;
      return { ...fallback, ...rest, madeFor, source, bodyType, sex, figure: figure ? { ...figure } : fallback.figure, values: { ...fallback.values, ...values }, estimated };
    }
  } catch { /* unavailable or corrupt: start from the default profile */ }
  return fallback;
}

function formatValue(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export default function App() {
  // Pattern pages without a pattern in the URL fall back to the specification currently being debugged.
  const initial = specifications.find((entry) => entry.specification.pattern.id === "tailored-shirt-block") ?? specifications[0];
  /** The print set the URL is in, if any: a preset or the library's group. */
  const activeSet = createMemo(() => { const current = route(); return current.name === "set" ? printSetById(current.setId) : undefined; });
  const inSetWorkspace = () => { const current = route(); return current.name === "set" && current.view === "workspace" && activeSet() !== undefined; };
  const view = (): PatternView | undefined => { const current = route(); return current.name === "pattern" ? current.view : inSetWorkspace() ? "workspace" : undefined; };
  const routePatternId = () => {
    const current = route();
    // A set's workspace opens its first garment until another is chosen.
    if (current.name === "set") return inSetWorkspace() ? current.patternId ?? activeSet()!.members[0]?.patternId : undefined;
    return current.name === "pattern" || current.name === "measurements" ? current.patternId : undefined;
  };
  // Garments of the open set looked at in its workspace this visit, ticked on their tabs.
  const [seenInSet, setSeenInSet] = createSignal<string[]>([]);
  const [source, setSource] = createSignal(initial.source);
  const [appliedSource, setAppliedSource] = createSignal(initial.source);
  const [specification, setSpecification] = createSignal(initial.specification);
  const [body, setBody] = createSignal(loadSavedBody());
  const [includeInlay, setIncludeInlay] = createSignal(loadIncludeInlay());
  /** Pattern defaults, with every measurement the body set covers filled from it. */
  const projectFor = (spec: typeof initial.specification) => projectForPattern(spec, body(), includeInlay());
  const [project, setProject] = createSignal(projectFor(initial.specification));
  // Kept per pattern, so it reopens as it was left and print sets draft it the same way.
  createEffect(() => savePatternSettings(project()));
  const [printExcludedByPattern, setPrintExcludedByPattern] = createSignal(loadPrintExcluded());
  const printExcluded = () => printExcludedByPattern()[specification().pattern.id] ?? [];
  const togglePrint = (blockId: string) => {
    const patternId = specification().pattern.id;
    const current = printExcluded();
    const next = { ...printExcludedByPattern(), [patternId]: current.includes(blockId) ? current.filter((id) => id !== blockId) : [...current, blockId] };
    setPrintExcludedByPattern(next);
    try { localStorage.setItem(printExcludedStorageKey, JSON.stringify(next)); } catch { /* storage unavailable: keep in memory only */ }
  };
  const [diagnostics, setDiagnostics] = createSignal<Diagnostic[]>([]);
  const [selected, setSelected] = createSignal<WorkspaceSelection>();
  const [showGrid, setShowGrid] = createSignal(true);
  const [showPoints, setShowPoints] = createSignal(true);
  const [showCurveHandles, setShowCurveHandles] = createSignal(true);
  const [showMannequin, setShowMannequin] = createSignal(true);
  const [savedProfiles, setSavedProfiles] = createSignal(loadSavedProfiles());
  createEffect(() => {
    try { localStorage.setItem(profilesStorageKey, JSON.stringify(savedProfiles())); } catch { /* storage unavailable: keep in memory only */ }
  });
  createEffect(() => {
    const current = body();
    try { localStorage.setItem(bodyStorageKey, JSON.stringify(current)); } catch { /* storage unavailable: keep in memory only */ }
  });
  const draft = createMemo(() => draftPattern(specification(), project()));
  const dirty = createMemo(() => source() !== appliedSource());

  const [guideStep, setGuideStep] = createSignal<GuideStep>();
  // The pattern picked on the library screen, which may be filtered away from the one last opened.
  const [libraryPick, setLibraryPick] = createSignal<string>();
  const libraryPickName = () => specifications.find((entry) => entry.specification.pattern.id === libraryPick())?.specification.pattern.name;
  const currentStep = (): GuideStep | undefined => {
    const current = route();
    if (current.name === "measurements" || current.name === "library") return current.name;
    if (current.name === "set") return current.view === "workspace" ? "workspace" : "print";
    return current.name === "pattern" ? current.view : undefined;
  };
  const stepHref = (step: GuideStep) => {
    if (step === "measurements" || step === "library") return routePath({ name: step });
    // Inside a set, the workspace and print steps are the set's.
    const current = route(), set = activeSet();
    if (current.name === "set" && set) return routePath({ name: "set", setId: current.setId, view: step, patternId: step === "workspace" && set.members.some((member) => member.patternId === specification().pattern.id) ? specification().pattern.id : undefined });
    return routePath({ name: "pattern", patternId: specification().pattern.id, view: step });
  };
  const nextHref = (step: GuideStep) => {
    if (step === "library") { const id = libraryPick(); return id ? routePath({ name: "pattern", patternId: id, view: "workspace" }) : undefined; }
    const next = guideSteps[guideSteps.indexOf(step) + 1];
    return next ? stepHref(next) : undefined;
  };
  const closeGuide = () => {
    setGuideStep(undefined);
    try { localStorage.setItem(guideSeenStorageKey, "1"); } catch { /* storage unavailable: the guide opens again next visit */ }
  };

  // On a first visit the guide opens at the first step reached, not over the start screen.
  const [guidePending, setGuidePending] = createSignal(false);
  onMount(() => {
    const saved = localStorage.getItem(recoveryStorageKey) ?? localStorage.getItem(legacyRecoveryStorageKey);
    if (saved) setSource(saved);
    let seen = false;
    try { seen = localStorage.getItem(guideSeenStorageKey) !== null; } catch { /* treat as a first visit */ }
    if (!seen) setGuidePending(true);
  });
  createEffect(() => {
    if (!guidePending() || route().name === "start") return;
    setGuidePending(false);
    setGuideStep(currentStep() ?? "measurements");
  });

  const [unknownPattern, setUnknownPattern] = createSignal<string>();

  const load = (id: string) => {
    const entry = specifications.find((item) => item.specification.pattern.id === id);
    if (!entry) return false;
    setSource(entry.source);
    setAppliedSource(entry.source);
    setSpecification(entry.specification);
    setProject(projectFor(entry.specification));
    setDiagnostics([]);
    setSelected(undefined);
    return true;
  };

  // The URL owns which pattern is open. An applied edit that keeps the same id
  // is left alone, so editing a specification does not reset it from the library.
  createEffect(() => {
    const patternId = routePatternId();
    if (inSetWorkspace() && patternId && !seenInSet().includes(patternId)) setSeenInSet([...seenInSet(), patternId]);
    if (patternId === undefined || patternId === specification().pattern.id) { setUnknownPattern(undefined); return; }
    setUnknownPattern(load(patternId) ? undefined : patternId);
  });

  const validate = () => {
    // Parsed with the module registry, so specs that use modules resolve.
    const result = parsePattern(source());
    setDiagnostics(result.diagnostics);
    localStorage.setItem(recoveryStorageKey, source());
    localStorage.removeItem(legacyRecoveryStorageKey);
    return result;
  };

  const apply = () => {
    const result = validate();
    if (!result.specification) return;
    const previous = project();
    const next = projectFor(result.specification);
    next.bodyMeasurements = { ...previous.bodyMeasurements };
    if (previous.specification.id === result.specification.pattern.id) {
      for (const id of Object.keys(next.measurements)) if (previous.measurements[id] !== undefined) next.measurements[id] = previous.measurements[id];
      // A kept value must still suit the option: a choice key it still offers, or a number.
      for (const [id, definition] of Object.entries(result.specification.options)) if (previous.options[id] !== undefined) next.options[id] = normaliseOptionValue(definition, previous.options[id]);
    }
    setSpecification(result.specification);
    setProject(next);
    setAppliedSource(source());
    // A renamed specification moves to its own URL without reloading it from the library.
    const current = route();
    if (current.name === "pattern" && current.patternId !== result.specification.pattern.id) navigate({ name: "pattern", patternId: result.specification.pattern.id, view: current.view }, { replace: true });
    if (current.name === "measurements" && current.patternId && current.patternId !== result.specification.pattern.id) navigate({ name: "measurements", patternId: result.specification.pattern.id }, { replace: true });
  };

  /** A body measurement changed: store it once and refill every pattern measurement that reads it. */
  const changeBodyValue = (id: string, value: number) => {
    setBody((current) => ({ ...current, source: { kind: "custom" }, madeFor: editedName(current), values: { ...current.values, [id]: value }, estimated: current.estimated.filter((item) => item !== id) }));
    setProject((current) => ({ ...current, madeFor: body().madeFor, measurements: { ...current.measurements, ...linkedMeasurements(specification(), body().values) } }));
  };

  const changeValue = (collection: "measurements" | "options", id: string, value: OptionValue) => {
    const bodyId = collection === "measurements" ? bodyMeasurementFor(id) : undefined;
    if (bodyId) { changeBodyValue(bodyId, Number(value)); return; }
    if (collection === "measurements") setBody((current) => ({ ...current, source: { kind: "custom" }, madeFor: editedName(current) }));
    setProject((current) => ({ ...current, ...(collection === "measurements" ? { madeFor: body().madeFor } : {}), [collection]: { ...current[collection], [id]: value } }));
  };

  const changeIncludeInlay = (include: boolean) => {
    setIncludeInlay(include);
    setProject((current) => ({ ...current, includeInlay: include }));
    try { localStorage.setItem(inlayStorageKey, include ? "1" : "0"); } catch { /* storage unavailable: keep in memory only */ }
  };

  const changeBodyMeasurement = (id: keyof BodyMeasurements, value: number) => {
    setBody((current) => ({ ...current, source: { kind: "custom" }, madeFor: editedName(current), figure: { ...project().bodyMeasurements, [id]: value } }));
    setProject((current) => ({ ...current, madeFor: body().madeFor, bodyMeasurements: { ...current.bodyMeasurements, [id]: value } }));
  };

  /** A body chosen whole (a size, an example or a saved profile): every pattern measurement is refilled from it. */
  const applyBody = (next: SavedBody) => {
    setBody((current) => ({ ...next, figure: next.figure ?? current.figure }));
    setProject((current) => ({
      ...current,
      madeFor: body().madeFor,
      bodyMeasurements: body().figure ? { ...body().figure! } : current.bodyMeasurements,
      measurements: { ...defaultsFor(specification()).measurements, ...linkedMeasurements(specification(), body().values) },
    }));
  };
  const chooseBodyType = (type: BodyType) => { if (body().bodyType !== type) applyBody(bodyForType(type)); };
  const chooseChartSize = (chartId: string, sizeId: string, stature: ChartStature["id"]) => { const next = bodyFromChart(chartId, sizeId, stature); if (next) applyBody(next); };
  const chooseSavedProfile = (id: string) => {
    const saved = savedProfiles().find((profile) => profile.id === id);
    if (!saved) return;
    const { id: _id, from: _from, ...rest } = saved;
    applyBody({ ...rest, source: { kind: "saved", id } });
  };
  /** Save the open body by name; a name already saved for this body type is updated. */
  const saveProfile = (name: string) => {
    const current = body();
    const existing = savedProfiles().find((profile) => profile.madeFor === name && profile.bodyType === current.bodyType);
    const id = existing?.id ?? `${Date.now().toString(36)}`;
    const from = current.source.kind === "custom" ? existing?.from ?? current.madeFor : current.madeFor;
    const { source: _source, ...rest } = current;
    const profile: SavedProfile = { ...rest, madeFor: name, id, from };
    setSavedProfiles([...savedProfiles().filter((item) => item.id !== id), profile]);
    setBody({ ...current, madeFor: name, source: { kind: "saved", id } });
    setProject((project) => ({ ...project, madeFor: name }));
  };
  const deleteSavedProfile = (id: string) => {
    setSavedProfiles(savedProfiles().filter((profile) => profile.id !== id));
    if (body().source.kind === "saved" && (body().source as { id: string }).id === id) setBody((current) => ({ ...current, source: { kind: "custom" } }));
  };
  // Projects and the skills practised, kept between visits.
  const [projects, setProjects] = createSignal<Project<SavedBody>[]>(loadProjects() as Project<SavedBody>[]);
  createEffect(() => saveProjects(projects()));
  const [skillProgress, setSkillProgress] = createSignal(loadSkillProgress());
  createEffect(() => saveSkillProgress(skillProgress()));
  const raise = (skillId: string, level: SkillLevel) => setSkillProgress((current) => raiseSkill(current, skillId, level));
  const lastProject = () => [...projects()].sort((a, b) => b.updated - a.updated)[0];
  const createProject = (recipe: NonNullable<ReturnType<typeof recipeById>>, choices: Record<string, string>, toile: boolean, name: string) => {
    const project = { ...newProject(recipe, body().madeFor, body(), choices, toile), name };
    setProjects([...projects(), project]);
    navigate({ name: "projects", projectId: project.id });
  };
  /** Saves a project; a step newly ticked off counts its skill as used. */
  const changeProject = (next: Project) => {
    const previous = projects().find((project) => project.id === next.id);
    for (const step of projectPlan(next)) if (step.skill && next.done.includes(step.id) && !previous?.done.includes(step.id)) raise(step.skill, "used");
    setProjects(projects().map((project) => (project.id === next.id ? (next as Project<SavedBody>) : project)));
  };
  /** Opens a project's pattern drafted for its body, with its design's options set. */
  const openProjectDraft = (project: Project, view: "workspace" | "print") => {
    const recipe = recipeById(project.recipeId);
    if (!recipe) return;
    applyBody(project.body as SavedBody);
    if (!load(recipe.patternId)) return;
    // Toile changes go on top of the body's measurements, for this project's draft only.
    const adjust = draftAdjustments(project);
    setProject((current) => ({
      ...current,
      options: { ...current.options, ...optionsFor(recipe, project.choices) },
      measurements: { ...current.measurements, ...Object.fromEntries(Object.entries(adjust).flatMap(([id, amount]) => (typeof current.measurements[id] === "number" ? [[id, current.measurements[id] + amount]] : []))) },
    }));
    navigate({ name: "pattern", patternId: recipe.patternId, view });
  };

  const exampleProfiles = bodyTypes.flatMap((type) => type.examples.map((id) => ({ id, label: measurementProfiles[id].label, description: measurementProfiles[id].description, bodyType: type.id })));
  const savedSummaries = (): SavedProfileSummary[] => savedProfiles().map((profile) => ({ id: profile.id, name: profile.madeFor, bodyType: profile.bodyType, from: profile.from }));

  const download = (name: string, contents: string, type: string) => {
    const url = URL.createObjectURL(new Blob([contents], { type }));
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    link.click();
    URL.revokeObjectURL(url);
  };

  const startItems = (): StartMenuItem[] => {
    const id = specification().pattern.id, name = specification().pattern.name;
    const last = lastProject(), next = last && nextStep(last), progress = last && progressOf(last);
    const practised = skills.filter((skill) => ["practised", "used"].includes(skillProgress()[skill.id] ?? "new")).length;
    return [
      last
        ? { label: "Continue project", detail: `${last.name}: ${progress!.done} of ${progress!.total} steps${next ? `, next ${next.title.toLowerCase()}` : ", finished"}`, href: routePath({ name: "projects", projectId: last.id }) }
        : { label: "Continue project", detail: "Start a project to pick up where you left off", locked: true },
      { label: "New project", detail: "Pick a pattern, a size and a design, then make it step by step", href: routePath({ name: "new-project" }) },
      { label: "Pattern library", detail: `${specifications.length} patterns from the cutting books`, href: routePath({ name: "library" }) },
      { label: "Skills", detail: `${skills.length} skills, ${practised} practised: pockets, flies, canvas and more`, href: routePath({ name: "skills" }) },
      { label: "Measurements", detail: `Drafting for ${body().madeFor}: choose a size or measure`, href: routePath({ name: "measurements" }) },
      { label: "Continue drafting", detail: name, href: routePath({ name: "pattern", patternId: id, view: "workspace" }) },
      { label: "Print", detail: `${name}, tiled on A4 or A3`, href: routePath({ name: "pattern", patternId: id, view: "print" }) },
      { label: "Print sets", detail: `${printSets.map((set) => set.name).join(", ")} and your own groups`, href: routePath({ name: "set", setId: printSets[0].id, view: "print" }) },
      { label: "Settings", detail: "Units, paper, sign-in and sync", locked: true },
    ];
  };

  return (
    <Show when={route().name !== "start"} fallback={<StartPage items={startItems()} version={__APP_VERSION__} />}>
    <div class="app-shell">
      <header class="topbar">
        <div><span class="eyebrow">TAILORING CAD</span><h1><a class="brand" href="/" onClick={linkClick} title="Start screen">Auto Draft</a></h1></div>
        <nav aria-label="Steps">
          <For each={guideSteps}>
            {(step, index) => {
              const done = () => currentStep() !== undefined && index() < guideSteps.indexOf(currentStep()!);
              return <span class="nav-step" classList={{ done: done() }}>
              <a href={stepHref(step)} classList={{ active: currentStep() === step }} aria-current={currentStep() === step ? "step" : undefined} onClick={linkClick}>
                <span class="step-badge" title={done() ? "Done" : `Step ${index() + 1}`}><Show when={done()} fallback={<StepIcon step={step} size={16} />}><CheckIcon size={14} /></Show></span>
                <span class="step-label"><small>{index() + 1}</small>{step === "library" ? "patterns" : step}</span>
              </a>
            </span>;
            }}
          </For>
        </nav>
        <div class="status"><span classList={{ stale: dirty() }}>{dirty() ? "Unapplied edits" : "Draft current"}</span></div>
      </header>
      <Show when={currentStep()}>{(step) => <GoalBar step={step()} nextLabel={step() === "library" ? (libraryPickName() ? `Continue with ${libraryPickName()}` : "") : step() === "workspace" && inSetWorkspace() ? `Print ${activeSet()!.name.toLowerCase()}` : undefined} nextHref={nextHref(step())} onNext={() => window.print()} onHelp={() => setGuideStep(step())} />}</Show>
      <GuideDialog step={guideStep()} hrefFor={stepHref} onStep={setGuideStep} onClose={closeGuide} />

      <Switch>
      <Match when={route().name === "not-found" || unknownPattern()}>
        <main class="page-content"><div class="page-heading"><p class="eyebrow">NOT FOUND</p><h2>{unknownPattern() ? `No pattern called “${unknownPattern()}”` : "No page at this address"}</h2><p><a href="/patterns" onClick={linkClick}>Back to the pattern library</a></p></div></main>
      </Match>

      <Match when={route().name === "library"}>
        <PatternLibrary initialId={specification().pattern.id} bodyType={body().bodyType} onSelect={setLibraryPick} />
      </Match>

      <Match when={route().name === "measurements"}>
        <FitPage
          specification={specification()}
          project={project()}
          draft={draft()}
          bodyType={body().bodyType}
          bodySource={body().source}
          madeFor={body().madeFor}
          examples={exampleProfiles}
          savedProfiles={savedSummaries()}
          onBodyType={chooseBodyType}
          onChartSize={chooseChartSize}
          onExample={(id) => applyBody(bodyFromProfile(id as BodyProfileId))}
          onSavedProfile={chooseSavedProfile}
          onDeleteSavedProfile={deleteSavedProfile}
          onSaveProfile={saveProfile}
          onMeasurement={(id, value) => changeValue("measurements", id, value)}
          body={body().values}
          bodyRows={bodyMeasurementRows}
          estimated={body().estimated}
          onBodyValue={changeBodyValue}
          sex={body().sex}
          workspaceHref={routePath({ name: "pattern", patternId: specification().pattern.id, view: "workspace" })}
          onBodyMeasurement={changeBodyMeasurement}
          onDownloadProject={() => download(`${project().name}.json`, JSON.stringify({ ...project(), body: body().values }, null, 2), "application/json")}
          source={source()}
          onSource={setSource}
          onValidate={validate}
          onApply={apply}
          onDownloadSpecification={() => download(`${specification().pattern.id}.yaml`, source(), "text/yaml")}
          diagnostics={diagnostics()}
          dirty={dirty()}
        />
      </Match>

      <Match when={view() === "workspace"}>
        <main class="workspace-page">
          <StylePanel specification={specification()} project={project()} measurementsHref={routePath({ name: "measurements" })} pieceCount={draft().blocks.length} onOption={(id, value) => changeValue("options", id, value)} onIncludeInlay={changeIncludeInlay} />
          <CadWorkspace draft={draft()} showGrid={showGrid()} showMannequin={showMannequin()} showPoints={showPoints()} showCurveHandles={showCurveHandles()} selectedId={selected()?.id} onSelect={setSelected} printExcluded={printExcluded()} onTogglePrint={togglePrint} design={<>
            <Show when={inSetWorkspace() ? activeSet() : undefined}>{(set) => <nav class="garment-tabs" aria-label={`Garments in ${set().name}`}>
              <For each={set().members}>{(member) => {
                const active = () => member.patternId === specification().pattern.id;
                return <a href={routePath({ name: "set", setId: set().id, view: "workspace", patternId: member.patternId })} classList={{ active: active() }} aria-current={active() ? "page" : undefined} title={seenInSet().includes(member.patternId) ? `${member.name}: looked at` : member.name} onClick={linkClick}><strong>{member.code}</strong>{member.name}<Show when={seenInSet().includes(member.patternId) && !active()}><CheckIcon size={12} /></Show></a>;
              }}</For>
            </nav>}</Show>
            {/* Design choices with a long list (such as the front closing), to swap designs without leaving the canvas. */}
            <For each={Object.entries(specification().options).filter(([, definition]) => definition.type === "choice" && !definition.hidden && Object.keys(definition.choices).length > 4)}>{([id, definition]) => {
              const choices = () => definition.type === "choice" ? definition.choices : {};
              const current = () => {
                const value = String(project().options[id]);
                return value in choices() ? value : String(definition.default);
              };
              return (
                <label class="design-choice">
                  <span>{definition.label}</span>
                  <select value={current()} onChange={(event) => changeValue("options", id, event.currentTarget.value)}>
                    <For each={Object.entries(choices())}>{([key, label]) => <option value={key} selected={current() === key}>{label}</option>}</For>
                  </select>
                </label>
              );
            }}</For>
          </>} tools={
            <div class="display-toggles" role="group" aria-label="Display">
              <button type="button" classList={{ active: showMannequin() }} aria-pressed={showMannequin()} title="Body outline on each piece: the gap to the cut line is the ease" onClick={() => setShowMannequin(!showMannequin())}>Body</button>
              <button type="button" classList={{ active: showGrid() }} aria-pressed={showGrid()} onClick={() => setShowGrid(!showGrid())}>Grid</button>
              <button type="button" classList={{ active: showPoints() }} aria-pressed={showPoints()} onClick={() => setShowPoints(!showPoints())}>Points</button>
              <button type="button" classList={{ active: showCurveHandles() }} aria-pressed={showCurveHandles()} onClick={() => setShowCurveHandles(!showCurveHandles())}>Handles</button>
            </div>
          } />
          <aside class="workspace-details" aria-label="Pattern details"><Show when={selected()}>{(selection) => <div class="inspector"><p class="eyebrow">SELECTED {selection().kind}</p><strong>{selection().id}</strong><Show when={selection().position}>{(point) => <code>x {point()[0].toFixed(2)}<br />y {point()[1].toFixed(2)}</code>}</Show><Show when={selection().kind === "point" ? draft().provenance[selection().id] : undefined}>{(info) => <div class="provenance"><Show when={info().label}><p class="provenance-label">{info().label}</p></Show><Show when={info().source}><blockquote>{info().source}</blockquote></Show><p>{info().construction}</p><Show when={info().fields.length > 0}><dl><For each={info().fields}>{(field) => <div><dt>{field.field}</dt><dd><Show when={field.reference !== String(field.value)}><code>{field.reference}</code></Show><span>{formatValue(field.value)}</span></dd></div>}</For></dl></Show><Show when={info().dependsOnPoints.length > 0}><p class="eyebrow">BUILT FROM</p><div class="provenance-points"><For each={info().dependsOnPoints}>{(pointId) => <button type="button" onClick={() => setSelected({ id: pointId, kind: "point", position: draft().points[pointId] ? [...draft().points[pointId]] : undefined })}>{pointId}</button>}</For></div></Show><Show when={Object.keys(info().dependsOnValues).length > 0}><p class="eyebrow">READS</p><dl><For each={Object.entries(info().dependsOnValues)}>{([name, value]) => <div><dt>{name}</dt><dd><span>{formatValue(value)}</span></dd></div>}</For></dl></Show></div>}</Show></div>}</Show><p class="eyebrow">PIECES</p><ul class="piece-legend"><For each={draft().blocks}>{(block, index) => {
            const printed = () => !printExcluded().includes(block.id);
            return <li classList={{ "not-printed": !printed() }}><span class="swatch" style={{ background: pieceColourCss(index()) }} />{block.label}<small>×{block.cutQuantity}</small><button type="button" class="print-toggle" aria-pressed={printed()} aria-label={`${printed() ? "Leave out of" : "Include in"} the print: ${block.label}`} title={printed() ? "Printed: click to leave out" : "Not printed: click to include"} onClick={() => togglePrint(block.id)}><Show when={printed()} fallback={<NoPrintIcon size={15} />}><PrintIcon size={15} /></Show></button></li>;
          }}</For></ul><p class="piece-counts"><strong>{Object.keys(draft().points).length}</strong> points<br /><strong>{draft().edges.length}</strong> edges<br /><strong>{draft().blocks.length}</strong> blocks</p><For each={draft().seamComparisons}>{(comparison) => <div class="seam-comparison"><p class="eyebrow">SEW-LINE FIT</p><strong>{comparison.label}</strong><dl><div><dt>{comparison.first.label}</dt><dd>{comparison.first.length.toFixed(1)} mm</dd></div><div><dt>{comparison.second.label}</dt><dd>{comparison.second.length.toFixed(1)} mm</dd></div><div><dt>Difference</dt><dd classList={{ match: comparison.matches, mismatch: !comparison.matches }}>{comparison.difference > 0 ? "+" : ""}{comparison.difference.toFixed(1)} mm</dd></div></dl><small>{comparison.matches ? `Match within ${comparison.tolerance} mm` : comparison.difference > 0 ? "Sleeve head is longer" : "Sleeve head is shorter"}</small></div>}</For><Show when={draft().assertions.length > 0}><div class="seam-comparison assertion-list"><p class="eyebrow">BOOK CHECKS</p><strong>{draft().assertions.filter((assertion) => assertion.passes).length} of {draft().assertions.length} pass</strong><ul><For each={draft().assertions}>{(assertion) => <li classList={{ match: assertion.passes, mismatch: !assertion.passes }} title={[assertion.source, assertion.message].filter(Boolean).join(" · ")}><span>{assertion.passes ? "✓" : "✗"}</span>{assertion.label}</li>}</For></ul></div></Show><For each={draft().diagnostics}>{(item) => <p class={item.severity}>{item.message}</p>}</For></aside>
        </main>
      </Match>

      <Match when={(() => { const current = route(); return current.name === "set" && !inSetWorkspace() ? current : undefined; })()}>{(current) =>
        <main class="page-content"><Show when={printSetById(current().setId)} fallback={<div class="page-heading"><p class="eyebrow">NOT FOUND</p><h2>No print set called “{current().setId}”</h2><p><a href="/patterns" onClick={linkClick}>Back to the pattern library</a></p></div>}>{(set) =>
          <PrintSetPage set={set()} view={current().view === "layout" ? "layout" : "print"} body={body()} includeInlay={includeInlay()} excludedByPattern={printExcludedByPattern()} />
        }</Show></main>
      }</Match>

      <Match when={(() => { const current = route(); return current.name === "pockets" ? current : undefined; })()}>{(current) =>
        <PocketWizard pocketId={current().pocketId} step={current().step} body={body()} includeInlay={includeInlay()} onFinished={(guideId) => { const skill = skillForGuide(guideId); if (skill) raise(skill.id, "watched"); }} />
      }</Match>

      <Match when={route().name === "skills"}>
        <SkillsPage skillProgress={skillProgress()} onPractised={(id) => raise(id, "practised")} />
      </Match>

      <Match when={(() => { const current = route(); return current.name === "new-project" ? current : undefined; })()}>{(current) =>
        <NewProjectPage recipeId={current().recipeId} madeFor={body().madeFor} skillProgress={skillProgress()} onCreate={createProject} />
      }</Match>

      <Match when={(() => { const current = route(); return current.name === "projects" ? current : undefined; })()}>{(current) =>
        <Show when={current().projectId} fallback={<ProjectsPage projects={projects()} />}>{(projectId) =>
          <ProjectPage
            projectId={projectId()}
            project={projects().find((project) => project.id === projectId())}
            skillProgress={skillProgress()}
            onChange={changeProject}
            onDelete={(id) => { setProjects(projects().filter((project) => project.id !== id)); navigate({ name: "projects" }); }}
            onOpenDraft={openProjectDraft}
            onPractised={(id) => raise(id, "practised")}
          />
        }</Show>
      }</Match>

      <Match when={view() === "print"}><main class="page-content"><PrintStudio garments={[{ draft: draft(), specification: specification(), project: project(), excluded: printExcluded() }]} title={specification().pattern.name} /></main></Match>
      </Switch>
    </div>
    </Show>
  );
}
